import React from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { useFinanceMetrics } from '@/hooks/useFinanceAnalytics';

const MONTH_NAMES = ['Sty', 'Lut', 'Mar', 'Kwi', 'Maj', 'Cze', 'Lip', 'Sie', 'Wrz', 'Paź', 'Lis', 'Gru'];

const COLORS = {
  paid: '#22c55e',
  unpaid: '#f97316'
};

export default function RevenueChart({ branchId, year }) {
  const { data: monthlyData, isLoading, error } = useFinanceMetrics(branchId, year);

  // Transform data for chart
  const chartData = React.useMemo(() => {
    if (!monthlyData) return [];

    return monthlyData.map(item => ({
      month: MONTH_NAMES[item.month - 1],
      monthNum: item.month,
      paid: item.paid_amount_cents / 100,
      unpaid: item.unpaid_amount_cents / 100,
      total: item.billed_amount_cents / 100
    }));
  }, [monthlyData]);

  // Custom tooltip
  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) return null;

    const data = payload[0]?.payload;
    return (
      <div className="bg-white border border-slate-200 rounded-lg shadow-lg p-3">
        <p className="font-medium text-slate-900 mb-2">{label}</p>
        <div className="space-y-1 text-sm">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS.paid }} />
            <span className="text-slate-600">Opłacone:</span>
            <span className="font-medium">{data?.paid?.toLocaleString('pl-PL')} PLN</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS.unpaid }} />
            <span className="text-slate-600">Nieopłacone:</span>
            <span className="font-medium">{data?.unpaid?.toLocaleString('pl-PL')} PLN</span>
          </div>
          <div className="pt-1 border-t border-slate-100">
            <span className="text-slate-600">Razem:</span>
            <span className="font-medium ml-2">{data?.total?.toLocaleString('pl-PL')} PLN</span>
          </div>
        </div>
      </div>
    );
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-5 w-48" />
          <Skeleton className="h-4 w-64 mt-1" />
        </CardHeader>
        <CardContent>
          <Skeleton className="h-[300px] w-full" />
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="py-8">
          <p className="text-center text-slate-500">Nie udało się załadować danych o przychodach</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Przychody miesięczne</CardTitle>
        <CardDescription>
          Opłacone vs nieopłacone rachunki w {year} roku
        </CardDescription>
      </CardHeader>
      <CardContent>
        {chartData.length === 0 ? (
          <div className="flex items-center justify-center h-[300px] text-slate-500">
            Brak danych o przychodach
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis
                dataKey="month"
                tick={{ fill: '#64748b', fontSize: 12 }}
                axisLine={{ stroke: '#e2e8f0' }}
              />
              <YAxis
                tick={{ fill: '#64748b', fontSize: 12 }}
                axisLine={{ stroke: '#e2e8f0' }}
                tickFormatter={(value) => `${(value / 1000).toFixed(0)}k`}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend
                wrapperStyle={{ paddingTop: 20 }}
                formatter={(value) => (
                  <span className="text-sm text-slate-600">
                    {value === 'paid' ? 'Opłacone' : 'Nieopłacone'}
                  </span>
                )}
              />
              <Bar
                dataKey="paid"
                stackId="revenue"
                fill={COLORS.paid}
                radius={[0, 0, 0, 0]}
                name="paid"
              />
              <Bar
                dataKey="unpaid"
                stackId="revenue"
                fill={COLORS.unpaid}
                radius={[4, 4, 0, 0]}
                name="unpaid"
              />
            </BarChart>
          </ResponsiveContainer>
        )}
      </CardContent>
    </Card>
  );
}
